"use client";

import { c as _c } from "react/compiler-runtime";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import EHRAppointmentRemindersCard from "@/src/components/cards/ehr-cards/ehr-appointment-reminders-card";
import EHRBookedAppointmentsCard from "@/src/components/cards/ehr-cards/ehr-booked-appointments-card";
import EHRDocumentCard from "@/src/components/cards/ehr-cards/ehr-document-card";
import LocationCard from "@/src/components/cards/location-card/location-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import PractitionerCard from "@/src/components/cards/practitioner-card/practitioner-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { TaskStepCard } from "@/src/components/tasks/task-step-card";
import { useReferralLocationSwitchHandler, useReferralPractitionerSwitchHandler } from "@/src/hooks/use-entity-switch-handlers";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { Appointment } from "@/src/models/appointment";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { EHRDocument } from "@/src/models/ehr-document";
import { Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";
import { TaskStepCardStatus } from "@/src/models/task-step-card";
export interface AthenaReferralCardsData {
  patientDetails: Patient | null;
  coverages: Coverage[];
  careTeam: Practitioner[];
  appointmentReminders: Appointment[];
  bookedAppointments: Appointment[];
  document: EHRDocument | null;
  expectedDocument: EHRDocument | null;
  documentPatientId?: string | null;
}
interface AthenaReferralCardsProps {
  referral: Referral;
  athenaData: AthenaReferralCardsData | null;
  insuranceVerificationTaskId?: string;
}
export function AthenaReferralCards(t0) {
  const $ = _c(63);
  const {
    referral,
    athenaData,
    insuranceVerificationTaskId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referral.id);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = eligibilityCheck ? resolveStediEligibilityDisplay(eligibilityCheck).eligibilityStatus : undefined;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const status = t1;
  const eligibilityStatus = status === "active" || status === "inactive" ? status : undefined;
  const {
    createLocationSwitchHandler,
    isPending: isLocationPending
  } = useReferralLocationSwitchHandler();
  const {
    createPractitionerSwitchHandler,
    isPending: isPractitionerPending
  } = useReferralPractitionerSwitchHandler();
  const primaryCoverage = referral.primaryCoverage;
  const secondaryCoverage = referral.secondaryCoverage;
  let primaryEhrCoverage;
  let t2;
  if ($[2] !== athenaData?.coverages) {
    const ehrCoverages = [...(athenaData?.coverages || [])].sort(_temp);
    primaryEhrCoverage = ehrCoverages.find(_temp2) ?? ehrCoverages[0] ?? null;
    t2 = ehrCoverages.find(_temp3) ?? ehrCoverages[1] ?? null;
    $[2] = athenaData?.coverages;
    $[3] = primaryEhrCoverage;
    $[4] = t2;
  } else {
    primaryEhrCoverage = $[3];
    t2 = $[4];
  }
  const secondaryEhrCoverage = t2;
  let t3;
  if ($[5] !== eligibilityStatus || $[6] !== primaryCoverage || $[7] !== primaryEhrCoverage) {
    t3 = primaryCoverage && <CoverageCard key="coverage-primary" coverage={primaryCoverage} type="Primary" compareMode={true} secondaryCoverage={primaryEhrCoverage} isEditable={true} eligibilityStatus={eligibilityStatus} />;
    $[5] = eligibilityStatus;
    $[6] = primaryCoverage;
    $[7] = primaryEhrCoverage;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== secondaryCoverage || $[10] !== secondaryEhrCoverage) {
    t4 = secondaryCoverage && <CoverageCard key="coverage-secondary" coverage={secondaryCoverage} type="Secondary" compareMode={true} secondaryCoverage={secondaryEhrCoverage} isEditable={true} />;
    $[9] = secondaryCoverage;
    $[10] = secondaryEhrCoverage;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <div className="space-y-8">{t3}{t4}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const insuranceCards = t5;
  const t6 = athenaData?.document || null;
  const t7 = athenaData?.expectedDocument || null;
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <EHRDocumentCard document={t6} expectedDocument={t7} />;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== athenaData?.careTeam || $[19] !== createPractitionerSwitchHandler || $[20] !== isPractitionerPending || $[21] !== referral) {
    t9 = referral.referringProvider && <PractitionerCard practitioner={referral.referringProvider} type="Referring" compareMode={true} secondaryPractitioners={athenaData?.careTeam || null} onEditPractitioner={createPractitionerSwitchHandler(referral, "referringProvider")} onEditPractitionerLoading={isPractitionerPending} isEditable={true} />;
    $[18] = athenaData?.careTeam;
    $[19] = createPractitionerSwitchHandler;
    $[20] = isPractitionerPending;
    $[21] = referral;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== createLocationSwitchHandler || $[24] !== isLocationPending || $[25] !== referral) {
    t10 = referral.referringLocation && <LocationCard location={referral.referringLocation} type="Referring" onEditLocation={createLocationSwitchHandler(referral, "referringLocation")} onEditLocationLoading={isLocationPending} isEditable={true} />;
    $[23] = createLocationSwitchHandler;
    $[24] = isLocationPending;
    $[25] = referral;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = athenaData?.patientDetails;
  let t12;
  if ($[27] !== referral.patient || $[28] !== t11) {
    t12 = <PatientCard patient={referral.patient} compareMode={true} secondaryPatient={t11} />;
    $[27] = referral.patient;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== insuranceCards || $[31] !== insuranceVerificationTaskId || $[32] !== primaryCoverage) {
    t13 = insuranceVerificationTaskId ? <TaskStepCard header="Insurance" status={TaskStepCardStatus.VERIFICATION_REQUIRED} detail="Compare the primary and secondary insurance against Athena and the referral document, then mark the insurance correct or incorrect." taskId={insuranceVerificationTaskId} stepKey="insurance" workflowStepKeys={[StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_REFERRAL]}>{insuranceCards}{!primaryCoverage && <p className="text-sm text-muted-foreground">No primary insurance is recorded.</p>}</TaskStepCard> : insuranceCards;
    $[30] = insuranceCards;
    $[31] = insuranceVerificationTaskId;
    $[32] = primaryCoverage;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== athenaData?.appointmentReminders) {
    t14 = athenaData?.appointmentReminders || [];
    $[34] = athenaData?.appointmentReminders;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t14) {
    t15 = <EHRAppointmentRemindersCard appointmentReminders={t14} />;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== athenaData?.bookedAppointments) {
    t16 = athenaData?.bookedAppointments || [];
    $[38] = athenaData?.bookedAppointments;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t16) {
    t17 = <EHRBookedAppointmentsCard bookedAppointments={t16} />;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== createPractitionerSwitchHandler || $[43] !== isPractitionerPending || $[44] !== referral) {
    t18 = referral.receivingProvider && <PractitionerCard practitioner={referral.receivingProvider} type="Receiving" compareMode={false} onEditPractitioner={createPractitionerSwitchHandler(referral, "receivingProvider")} onEditPractitionerLoading={isPractitionerPending} isEditable={true} />;
    $[42] = createPractitionerSwitchHandler;
    $[43] = isPractitionerPending;
    $[44] = referral;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== createLocationSwitchHandler || $[47] !== isLocationPending || $[48] !== referral) {
    t19 = referral.receivingLocation && <LocationCard location={referral.receivingLocation} type="Receiving" onEditLocation={createLocationSwitchHandler(referral, "receivingLocation")} onEditLocationLoading={isLocationPending} isEditable={true} />;
    $[46] = createLocationSwitchHandler;
    $[47] = isLocationPending;
    $[48] = referral;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== referral) {
    t20 = <ReferralCard referral={referral} isEditable={true} />;
    $[50] = referral;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t10 || $[53] !== t12 || $[54] !== t13 || $[55] !== t15 || $[56] !== t17 || $[57] !== t18 || $[58] !== t19 || $[59] !== t20 || $[60] !== t8 || $[61] !== t9) {
    t21 = <>{t8}{t9}{t10}{t12}{t13}{t15}{t17}{t18}{t19}{t20}</>;
    $[52] = t10;
    $[53] = t12;
    $[54] = t13;
    $[55] = t15;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
    $[60] = t8;
    $[61] = t9;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  return t21;
}
function _temp3(cov_0) {
  return cov_0.order === 2;
}
function _temp2(cov) {
  return cov.order === 1;
}
function _temp(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}